<template>
  <div class="quiz-container">
    <h2 class="quiz-title">{{ quiz.title }}</h2>
    <p class="question-counter">Question {{ currentQuestionIndex + 1 }} of {{ quiz.questions.length }}</p>
    <div class="question-box">
      <h3 class="question">{{ quiz.questions[currentQuestionIndex].question }}</h3>
      <ul class="options-list">
        <li
          v-for="(option, index) in quiz.questions[currentQuestionIndex].options"
          :key="index"
          @click="selectOption(index)"
          :class="{ 'selected': selectedOptionIndex === index, 'correct': isAnswerRevealed && index === quiz.questions[currentQuestionIndex].correctAnswer, 'incorrect': isAnswerRevealed && selectedOptionIndex === index && index !== quiz.questions[currentQuestionIndex].correctAnswer }"
          class="option"
        >
          {{ option }}
          <span v-if="showFeedback && currentQuestion.correctAnswer === index" class="feedback-icon correct">✓</span>
      <span v-if="showFeedback && selectedOption === index && currentQuestion.correctAnswer !== index" class="feedback-icon incorrect">✗</span>
        </li>
      </ul>
    </div>
    <div v-if="isAnswerRevealed" class="feedback">
      <p :class="{ 'feedback-correct': isCorrect, 'feedback-incorrect': !isCorrect }">
        {{ feedbackMessage }}
      </p>
      
    </div>
    <div class="navigation-buttons">
      <button @click="previousQuestion" :disabled="currentQuestionIndex === 0" class="action-button">Back</button>
      <button @click="nextQuestion" :disabled="currentQuestionIndex === quiz.questions.length - 1 || !isAnswerRevealed" class="action-button">Next</button>
    </div>
    <div v-if="currentQuestionIndex === quiz.questions.length - 1 && isAnswerRevealed" class="results-summary">
      <h4>Quiz Summary</h4>
      <p>Correct answers: {{ correctAnswersCount }}</p>
      <p>Incorrect answers: {{ incorrectAnswersCount }}</p>
      <button @click="retakeQuiz" class="action-button">Retake Quiz</button>
      <button @click="goToNextQuiz" class="action-button next-quiz-button">Next Quiz</button>
    </div>
  </div><br><br><br><br><br><br>
</template>
  <script>
  import { ref } from 'vue';
  
  export default {
    setup() {
    
      const correctAnswerSound = new Audio('https://assets.codepen.io/11151897/nyse.m4a');
      const quiz = ref({
        title: "Introduction to Savings",
        questions: [
          {
            question: "What is the primary purpose of a savings account?",
            options: [
              "To invest in the stock market",
              "To save money for future use while earning interest",
              "To borrow money for large purchases",
              "To pay daily expenses"
            ],
            correctAnswer: 1
          },
          {
            question: "What is an 'emergency fund'?",
            options: [
              "Money saved for planned expenses like vacations",
              "A fund that the government provides during emergencies",
              "Money set aside for unexpected expenses",
              "A type of insurance policy"
            ],
            correctAnswer: 2
          },
          {
            question: "Why is it important to have savings?",
            options: [
              "To be able to lend money to others",
              "To have resources in case of emergencies and future needs",
              "To spend more on luxury items",
              "Savings have no real importance"
            ],
            correctAnswer: 1
          },
          {
            question: "What feature do high-interest savings accounts (HISA) offer?",
            options: [
              "Lower interest rates compared to regular savings accounts",
              "Higher interest rates for saving money",
              "The ability to invest in stocks and bonds",
              "Fixed term for withdrawing money"
            ],
            correctAnswer: 1
          },
          {
            question: "What is compound interest?",
            options: [
              "Interest earned only on the original amount of money saved",
              "Interest earned on both the original amount and on interest already earned",
              "A government tax on your savings",
              "Interest that remains constant over the life of the investment"
            ],
            correctAnswer: 1
          },
          {
            question: "How can automatic transfers help with saving?",
            options: [
              "By automatically transferring all your earnings into a savings account",
              "By scheduling regular transfers from checking to savings to build savings over time",
              "By transferring money to the government for taxes",
              "Automatic transfers are not beneficial for saving"
            ],
            correctAnswer: 1
          },
          {
            question: "What is a Registered Retirement Savings Plan (RRSP)?",
            options: [
              "A plan for saving towards purchasing a car",
              "A checking account with high fees",
              "A savings plan that offers tax benefits for retirement savings",
              "A government-provided pension plan"
            ],
            correctAnswer: 2
          },
          {
            question: "Which of the following is a good strategy for saving?",
            options: [
              "Spending your income before paying bills",
              "Saving as much money in one go as possible",
              "Setting aside a small, fixed amount of money regularly",
              "Using credit cards for all purchases to avoid spending cash"
            ],
            correctAnswer: 2
          },
          {
            question: "What role does inflation play in savings?",
            options: [
              "Inflation decreases the value of money over time, affecting the purchasing power of your savings",
              "Inflation increases the value of savings over time",
              "Inflation has no effect on savings",
              "Inflation benefits savers by decreasing the cost of goods and services"
            ],
            correctAnswer: 0
          },
          {
            question: "Why might someone choose a Tax-Free Savings Account (TFSA) over other savings options?",
            options: [
              "Because the interest earned is tax-free",
              "Because it offers the highest interest rate",
              "Because it allows unlimited withdrawals",
              "Because it is only available to high-income earners"
            ],
            correctAnswer: 0
          }
        ]
      });
      const currentQuestionIndex = ref(0);
      const correctAnswersCount = ref(0);
      const incorrectAnswersCount = ref(0);
      const selectedOptionIndex = ref(-1);
      const isAnswerRevealed = ref(false);
      const isCorrect = ref(false);
      const feedbackMessage = ref("");
  
      const selectOption = (index) => {
        if (isAnswerRevealed.value) return;
        selectedOptionIndex.value = index;
        isAnswerRevealed.value = true;
  
        if (index === quiz.value.questions[currentQuestionIndex.value].correctAnswer) {
          isCorrect.value = true;
          correctAnswersCount.value++;
          feedbackMessage.value = "Correct!";
          correctAnswerSound.play().catch(error => console.error("Audio play failed", error));
        } else {
          isCorrect.value = false;
          incorrectAnswersCount.value++;
          feedbackMessage.value = "Incorrect.";
        }
      };
  
      const nextQuestion = () => {
        if (currentQuestionIndex.value < quiz.value.questions.length - 1) {
          currentQuestionIndex.value++;
          resetState();
        }
      };
  
      const previousQuestion = () => {
        if (currentQuestionIndex.value > 0) {
          currentQuestionIndex.value--;
          resetState();
        }
      };
  
      const retakeQuiz = () => {
        currentQuestionIndex.value = 0;
        correctAnswersCount.value = 0;
        incorrectAnswersCount.value = 0;
        resetState();
      };
  
      const resetState = () => {
        selectedOptionIndex.value = -1;
        isAnswerRevealed.value = false;
        isCorrect.value = false;
        feedbackMessage.value = "";
      };
  
      return {
        quiz,
        currentQuestionIndex,
        correctAnswersCount,
        incorrectAnswersCount,
        selectedOptionIndex,
        isAnswerRevealed,
        isCorrect,
        feedbackMessage,
        selectOption,
        nextQuestion,
        previousQuestion,
        retakeQuiz,
      };
    },
  };
  </script>
  
<style scoped>
.quiz-container {
  color: #FFF;
  padding: 20px;
  border-radius: 10px;
  max-width: 600px;
  margin: auto;
  background: linear-gradient(135deg, #6D5BBA 0%, #8D58BF 100%);
}


.feedback-correct, .feedback-incorrect {
  padding: 5px 10px; /* Adds some padding around the text */
  border-radius: 15px; /* Rounds the corners for a bubble-like appearance */
  display: inline-block; /* Makes the background wrap the text */
  color: #FFF; /* Keeps text color white for high contrast */
  font-weight: bold; /* Ensure the text is bold */
  box-shadow: 0px 0px 3px rgba(0, 0, 0, 0.7); /* Optional: Adds a slight shadow for depth */
}

.feedback-correct {
  background-color: #4CAF50; /* Green background */
  border: 2px solid #FFF; /* White border for contrast */
  color: fff;
}

.feedback-incorrect {
  background-color: #F44336; /* Red background */
  border: 2px solid #FFF; /* White border for contrast */
}
.quiz-title, .question-counter, .question, .feedback {
  text-align: center;
  font-size: 1rem;
}

.results-summary {
  padding: 20px; /* Adds padding inside the summary box */
  background-color: rgba(255, 255, 255, 0.9); /* Semi-transparent white background */
  border-radius: 15px; /* Rounded corners */
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1); /* Soft shadow for depth */
  margin-top: 20px; /* Space above the summary */
  border: 2px solid #DDD; /* Light border for definition */
}

.results-summary h4 {
  color: #333; /* Darker text for contrast against light background */
  font-size: 1.5rem; /* Larger font size for the title */
}

.results-summary p {
  color: #555; /* Subtle text color for readability */
  font-size: 1rem; /* Larger font size for the content */
}

.options-list {
  list-style-type: none;
  padding: 0;
}

.option {
  margin: 10px 0;
  padding: 10px;
  color: black;
  background-color: #f4f4f8;
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.3s;
  font-size: 1rem;
}

.option:hover {
  background-color: #d0ccd3;
}

.option.selected.correct {
  border-color: green;
}

.option.selected.incorrect {
  border-color: red;
}
.button-container {
  display: flex;
  justify-content: center; /* Center the buttons horizontally */
  margin-top: 20px; /* Space above the button container */
}
.action-button {
  background-color: #4a148C; 
  color: #DDD;
  border: none;
  padding: 10px 20px;
  cursor: pointer;
  border-radius: 25px;
  font-weight: bold;
  transition: background-color 0.3s, color 0.3s;
  background-color: #36454f; /* Black background */
  color: white;
  justify-content: center; 
}


.action-button:hover {
  background-color: black; /* Dark purple background on hover */
}


.next-quiz-button {
  background-color: #4CAF50; /* Example green background */
  color: white;
  /* Add other styles as needed */
}

.centered-title {
  text-align: center;
  font-size: 2rem; /* Increase font size */
  color: #36454f; /* Dark text for contrast */
  margin-bottom: 20px; /* Add some space below the title */
}

.quiz-container h1 {
  font-size: 1.5rem; /* Make questions stand out */
  color: #4A148C; /* Use the dark purple for questions */
  margin-bottom: 15px;
}

.option-card {
  background-color: #f4f4f8;
  border: 1px solid #ccc;
  padding: 10px;
  margin: 5px 0;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color .3s, transform .2s;
}

.option-card:hover {
  background-color: #e9e9ef;
  transform: scale(1.02); /* Slightly enlarge options on hover */
}

.feedback-icon {
  font-weight: bold;
  margin-left: 15px;
}

.correct {
  color: green;
}

.incorrect {
  color: red;
}

.feedback {
  margin-top: 20px;
  text-align: center;
}

</style>